import { useLayoutEffect, useMemo, type RefObject } from "react";
import { useWindowOrigin } from "./use-window-origin";
import { useSettingsFrameStore } from "./settings-frame-store";
import { requestNativeGlassRefresh } from "./use-native-glass-surfaces";

export function useSettingsFrameSurface(
  surfaceRef: RefObject<HTMLDivElement | null>,
  open: boolean,
) {
  const sessionId = useSettingsFrameStore(
    (state) => state.frame?.sessionId ?? null,
  );
  const width = useSettingsFrameStore((state) => state.frame?.box.width);
  const maxHeight = useSettingsFrameStore((state) => state.frame?.box.height);
  const origin = useWindowOrigin();

  // Cursor geometry must not render the entire Settings form on each tick.
  // Keep the live glass surface mounted and move only its compositor transform.
  // A bottom-pinned pocket hangs from the box's bottom edge: shifting it up by
  // its own height lets CSS keep that edge still as the content height changes.
  useLayoutEffect(() => {
    if (!open || !sessionId) return;
    const paint = () => {
      const frame = useSettingsFrameStore.getState().frame;
      const element = surfaceRef.current;
      if (!frame || frame.sessionId !== sessionId || !element) return;
      const x = frame.box.x - origin.x;
      const y = frame.box.y - origin.y;
      const transform =
        frame.pinnedEdge === "bottom"
          ? `translate3d(${x}px, ${y + frame.box.height}px, 0) translateY(-100%)`
          : `translate3d(${x}px, ${y}px, 0)`;
      if (element.style.transform === transform) return;
      element.style.transform = transform;
      requestNativeGlassRefresh({ trackTransition: false });
    };
    paint();
    return useSettingsFrameStore.subscribe(paint);
  }, [open, sessionId, surfaceRef, origin.x, origin.y]);

  const style = useMemo(
    () =>
      sessionId
        ? { position: "fixed" as const, left: 0, top: 0, width, maxHeight }
        : null,
    [sessionId, width, maxHeight],
  );
  return {
    sessionId,
    style,
  };
}
